import RouteAppShell from '@/common/components/app-shell/RouteAppShell.tsx';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  ArrowCircleDownIcon,
  ArrowLeftIcon,
  ClipboardTextIcon,
  CopyIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useBlocker } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { nanoid } from 'nanoid';
import { type ClipboardEvent, useMemo, useState } from 'react';
import { z } from 'zod';
import { getClientControllerListClientsQueryOptions } from '@/generated/api/client.ts';
import {
  getLocationControllerGetLocationsQueryKey,
  getLocationControllerCreateLocationMutationOptions,
} from '@/generated/api/location.ts';

type RowStatus =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'success'; locationName: string | null }
  | { state: 'confirmation'; message: string }
  | { state: 'error'; message: string };

type LocationRow = {
  rowId: string;
  locationId: string;
  locationName: string;
  freeFormAddress: string;
  confirm: boolean;
  status: RowStatus;
  touched: Partial<
    Record<'locationId' | 'locationName' | 'freeFormAddress', boolean>
  >;
  showValidation: boolean;
};

const defaultRow = (): LocationRow => ({
  rowId: nanoid(),
  locationId: '',
  locationName: '',
  freeFormAddress: '',
  confirm: false,
  status: { state: 'idle' },
  touched: {},
  showValidation: false,
});

const rowSchema = z.object({
  locationId: z.string().trim().min(1),
  locationName: z.string().trim().min(1),
  freeFormAddress: z.string().trim().min(1),
});

// eslint-disable-next-line react-refresh/only-export-components
export const Route = createFileRoute('/data/create-locations')({
  component: CreateLocationsRoute,
  staticData: {
    authorization: {
      permission: {
        location: ['write'],
      },
    },
  },
});

function getErrorMessage(error: unknown) {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown } | undefined;
    if (typeof data?.message === 'string') return data.message;
  }
  return error instanceof Error ? error.message : String(error);
}

function isSubmittable(row: LocationRow) {
  if (row.status.state === 'success' || row.status.state === 'loading') {
    return false;
  }
  if (!rowSchema.safeParse(row).success) return false;
  return row.status.state !== 'confirmation' || row.confirm;
}

function hasDraftValue(row: LocationRow) {
  return Boolean(
    row.locationId.trim() ||
    row.locationName.trim() ||
    row.freeFormAddress.trim() ||
    row.status.state === 'confirmation' ||
    row.status.state === 'error',
  );
}

function parseLocationRows(text: string) {
  const parsedRows = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.split('\t').map((cell) => cell.trim()));
  const rowsWithoutHeader =
    parsedRows[0]?.some((cell) => /^(site|location)\s*id$/i.test(cell)) &&
    parsedRows[0]?.some((cell) => /^(site|location)\s*name$/i.test(cell))
      ? parsedRows.slice(1)
      : parsedRows;

  return rowsWithoutHeader
    .map(([locationId = '', locationName = '', ...addressParts]) => ({
      ...defaultRow(),
      locationId,
      locationName,
      freeFormAddress: addressParts.join(' ').trim(),
    }))
    .filter((row) => hasDraftValue(row));
}

function StatusBadge({ row }: { row: LocationRow }) {
  if (row.status.state === 'loading') {
    return <Badge color="blue">Submitting</Badge>;
  }
  if (row.status.state === 'success')
    return <Badge color="green">Created</Badge>;
  if (row.status.state === 'confirmation' && row.confirm) {
    return <Badge color="blue">Ready</Badge>;
  }
  if (row.status.state === 'confirmation') {
    return <Badge color="yellow">Confirm</Badge>;
  }
  if (row.status.state === 'error') return <Badge color="red">Error</Badge>;
  return <Badge color="gray">Draft</Badge>;
}

export default function CreateLocationsRoute() {
  const queryClient = useQueryClient();
  const clientsQuery = useQuery(
    getClientControllerListClientsQueryOptions({
      query: { staleTime: 'static' },
    }),
  );
  const createLocationMutation = useMutation(
    getLocationControllerCreateLocationMutationOptions(),
  );

  const [clientId, setClientId] = useState('');
  const [rows, setRows] = useState<LocationRow[]>([defaultRow()]);

  const clients = useMemo(
    () =>
      (clientsQuery.data?.items ?? []).map((client) => ({
        value: client.id,
        label: client.name,
      })),
    [clientsQuery.data],
  );
  const submitCount = useMemo(
    () => rows.filter((row) => isSubmittable(row)).length,
    [rows],
  );
  const isSubmitting = rows.some((row) => row.status.state === 'loading');
  const hasUnsavedDrafts = rows.some(
    (row) => row.status.state !== 'success' && hasDraftValue(row),
  );
  const batchSummary = useMemo(
    () =>
      rows.reduce(
        (summary, row) => {
          if (row.status.state === 'success') summary.created += 1;
          else if (row.status.state === 'loading') summary.submitting += 1;
          else if (row.status.state === 'error') summary.error += 1;
          else if (isSubmittable(row)) summary.ready += 1;
          else if (row.status.state === 'confirmation') summary.confirm += 1;
          else if (hasDraftValue(row)) summary.draft += 1;
          return summary;
        },
        { ready: 0, confirm: 0, error: 0, created: 0, draft: 0, submitting: 0 },
      ),
    [rows],
  );
  const submittedRowsCount =
    batchSummary.created + batchSummary.confirm + batchSummary.error;
  const submitLabel =
    submitCount > 0
      ? `Submit ${submitCount} Location${submitCount === 1 ? '' : 's'}`
      : 'Submit ready rows';

  useBlocker({
    shouldBlockFn: () => {
      if (!isSubmitting && !hasUnsavedDrafts) return false;
      const message = isSubmitting
        ? 'Location submission is still in progress. Leaving now could lose data. Leave anyway?'
        : 'This batch has unsaved location drafts. Leave without saving?';
      return !window.confirm(message);
    },
    enableBeforeUnload: () => isSubmitting || hasUnsavedDrafts,
  });

  const updateRow = (
    rowId: string,
    patch: Partial<LocationRow> | ((row: LocationRow) => Partial<LocationRow>),
  ) => {
    setRows((current) =>
      current.map((row) => {
        if (row.rowId !== rowId) return row;
        const nextPatch = typeof patch === 'function' ? patch(row) : patch;
        return { ...row, ...nextPatch };
      }),
    );
  };

  const submitRows = async () => {
    if (!clientId) {
      notifications.show({
        title: 'Choose a client',
        message: 'Select a client before submitting locations.',
        color: 'red',
      });
      return;
    }

    const rowsToSubmit = rows.filter((item) => isSubmittable(item));
    if (rowsToSubmit.length === 0) {
      setRows((current) =>
        current.map((row) =>
          row.status.state === 'success'
            ? row
            : { ...row, showValidation: true },
        ),
      );
      notifications.show({
        title: 'No ready locations',
        message: 'Complete at least one draft row before submitting.',
        color: 'red',
      });
      return;
    }

    for (const row of rowsToSubmit) {
      updateRow(row.rowId, { status: { state: 'loading' } });
      try {
        const result = await createLocationMutation.mutateAsync({
          data: {
            clientId,
            locationId: row.locationId,
            locationName: row.locationName,
            freeFormAddress: row.freeFormAddress,
            confirm: row.confirm,
          },
        });

        if (result.needsConfirmation) {
          updateRow(row.rowId, {
            freeFormAddress: result.formattedAddress,
            confirm: false,
            status: {
              state: 'confirmation',
              message:
                result.confirmationMessage ||
                'Confirm the normalized address and submit again.',
            },
            showValidation: false,
          });
          continue;
        }

        updateRow(row.rowId, {
          confirm: false,
          status: {
            state: 'success',
            locationName: result.locationName,
          },
          showValidation: false,
        });
      } catch (error) {
        updateRow(row.rowId, {
          status: { state: 'error', message: getErrorMessage(error) },
        });
      }
    }

    await queryClient.invalidateQueries({
      queryKey: getLocationControllerGetLocationsQueryKey(),
    });
  };

  const markTouched = (rowId: string, field: keyof LocationRow['touched']) => {
    updateRow(rowId, (row) => ({
      touched: { ...row.touched, [field]: true },
    }));
  };

  const deleteRow = (row: LocationRow) => {
    const index = rows.findIndex((item) => item.rowId === row.rowId);
    setRows((current) => current.filter((item) => item.rowId !== row.rowId));
    notifications.show({
      id: `delete-location-row-${row.rowId}`,
      title: 'Row deleted',
      message: (
        <Group gap="xs">
          <Text size="sm">The draft row was removed from this batch.</Text>
          <Button
            size="compact-xs"
            variant="subtle"
            onClick={() =>
              setRows((current) => [
                ...current.slice(0, index),
                row,
                ...current.slice(index),
              ])
            }
          >
            Undo
          </Button>
        </Group>
      ),
      color: 'gray',
      autoClose: 6000,
      withCloseButton: true,
    });
  };

  const appendPastedRows = (pastedRows: LocationRow[]) => {
    if (pastedRows.length === 0) {
      notifications.show({
        title: 'No rows found',
        message:
          'Paste rows with Location ID, Location Name, and Address columns.',
        color: 'red',
      });
      return;
    }

    setRows((current) => {
      const hasOnlyEmptyRow =
        current.length === 1 &&
        current[0].status.state === 'idle' &&
        !hasDraftValue(current[0]);
      return hasOnlyEmptyRow ? pastedRows : [...current, ...pastedRows];
    });
    notifications.show({
      title: 'Rows added',
      message: `${pastedRows.length} pasted location${
        pastedRows.length === 1 ? '' : 's'
      } added to this batch.`,
      color: 'green',
    });
  };

  const pasteRowsFromClipboard = async () => {
    if (!navigator.clipboard?.readText) {
      notifications.show({
        title: 'Clipboard unavailable',
        message: 'Paste tab-separated rows into the Location ID field instead.',
        color: 'red',
      });
      return;
    }

    try {
      appendPastedRows(parseLocationRows(await navigator.clipboard.readText()));
    } catch {
      notifications.show({
        title: 'Clipboard unavailable',
        message: 'Paste tab-separated rows into the Location ID field instead.',
        color: 'red',
      });
    }
  };

  const pasteRowsIntoTable = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text');
    if (!text.includes('\t') && !text.includes('\n')) return;
    event.preventDefault();
    appendPastedRows(parseLocationRows(text));
  };

  return (
    <RouteAppShell>
      <Stack h="100%" p="sm" gap="sm" style={{ minHeight: 0 }}>
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm" wrap="nowrap">
            <Button
              component={Link}
              to="/data/locations"
              variant="transparent"
              size="compact-lg"
              p={0}
              aria-label="Back to locations"
            >
              <Tooltip label="Back to locations">
                <ArrowLeftIcon />
              </Tooltip>
            </Button>
            <Text fw={700} size="sm">
              Create Locations
            </Text>
          </Group>
          <Group gap="xs" wrap="nowrap">
            <Button
              variant="light"
              leftSection={<ClipboardTextIcon size={16} />}
              disabled={isSubmitting}
              onClick={() => void pasteRowsFromClipboard()}
            >
              Paste rows
            </Button>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              onClick={() => void submitRows()}
            >
              {submitLabel}
            </Button>
          </Group>
        </Group>

        <Select
          label="Client"
          description="All rows in this batch will be created for this client."
          data={clients}
          value={clientId}
          loading={clientsQuery.isPending}
          disabled={isSubmitting}
          onChange={(value) => setClientId(value ?? '')}
          searchable
          allowDeselect={false}
        />

        {clientsQuery.isError ? (
          <Alert color="red" title="Unable to load clients">
            Refresh and try again. If this continues, contact support.
          </Alert>
        ) : null}

        <Paper withBorder p="xs" radius="sm">
          <Group gap="xs" justify="space-between" wrap="wrap">
            <Group gap="xs" wrap="wrap">
              <Badge variant="light" color="blue">
                {batchSummary.ready} ready
              </Badge>
              {batchSummary.submitting > 0 ? (
                <Badge variant="light" color="cyan">
                  {batchSummary.submitting} submitting
                </Badge>
              ) : null}
              <Badge variant="light" color="yellow">
                {batchSummary.confirm} to confirm
              </Badge>
              <Badge variant="light" color="red">
                {batchSummary.error} failed
              </Badge>
              <Badge variant="light" color="green">
                {batchSummary.created} created
              </Badge>
              <Badge variant="light" color="gray">
                {batchSummary.draft} draft
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              {isSubmitting
                ? `Submitting ${submittedRowsCount + 1} of ${rows.length}`
                : `${rows.length} row${rows.length === 1 ? '' : 's'} in batch`}
            </Text>
          </Group>
        </Paper>

        <Table.ScrollContainer minWidth={1100} style={{ flex: 1 }}>
          <Table
            withTableBorder
            withColumnBorders
            style={{ tableLayout: 'fixed' }}
          >
            <colgroup>
              <col style={{ width: '4%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '34%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '6%' }} />
            </colgroup>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>#</Table.Th>
                <Table.Th>Location ID</Table.Th>
                <Table.Th>Location Name</Table.Th>
                <Table.Th>Address</Table.Th>
                <Table.Th>Confirm</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row, index) => {
                const disabled =
                  row.status.state === 'loading' ||
                  row.status.state === 'success';
                const showLocationIdError =
                  !row.locationId.trim() &&
                  (row.touched.locationId || row.showValidation);
                const showLocationNameError =
                  !row.locationName.trim() &&
                  (row.touched.locationName || row.showValidation);
                const showAddressError =
                  !row.freeFormAddress.trim() &&
                  (row.touched.freeFormAddress || row.showValidation);

                return (
                  <Table.Tr key={row.rowId}>
                    <Table.Td valign="top">{index + 1}</Table.Td>
                    <Table.Td valign="top">
                      <TextInput
                        value={row.locationId}
                        aria-label={`Row ${index + 1} location ID`}
                        error={showLocationIdError ? 'Required' : undefined}
                        disabled={disabled}
                        onBlur={() => markTouched(row.rowId, 'locationId')}
                        onPaste={pasteRowsIntoTable}
                        onChange={(event) =>
                          updateRow(row.rowId, {
                            locationId: event.currentTarget.value,
                            status: { state: 'idle' },
                          })
                        }
                      />
                    </Table.Td>
                    <Table.Td valign="top">
                      <TextInput
                        value={row.locationName}
                        aria-label={`Row ${index + 1} location name`}
                        error={showLocationNameError ? 'Required' : undefined}
                        disabled={disabled}
                        onBlur={() => markTouched(row.rowId, 'locationName')}
                        onChange={(event) =>
                          updateRow(row.rowId, {
                            locationName: event.currentTarget.value,
                            status: { state: 'idle' },
                          })
                        }
                      />
                    </Table.Td>
                    <Table.Td valign="top">
                      <Textarea
                        value={row.freeFormAddress}
                        autosize
                        minRows={2}
                        aria-label={`Row ${index + 1} address`}
                        error={showAddressError ? 'Required' : undefined}
                        disabled={disabled}
                        onBlur={() => markTouched(row.rowId, 'freeFormAddress')}
                        onChange={(event) =>
                          updateRow(row.rowId, {
                            freeFormAddress: event.currentTarget.value,
                            confirm: false,
                            status: { state: 'idle' },
                          })
                        }
                      />
                      {row.status.state === 'confirmation' ? (
                        <Alert
                          color={row.confirm ? 'green' : 'yellow'}
                          mt={6}
                          p="xs"
                          title={
                            row.confirm
                              ? 'Normalized address accepted'
                              : 'Review normalized address'
                          }
                        >
                          <Stack gap={4}>
                            <Text size="xs">{row.status.message}</Text>
                            <Text size="xs" fw={600}>
                              {row.freeFormAddress}
                            </Text>
                            <Button
                              size="compact-xs"
                              variant="light"
                              color={row.confirm ? 'green' : 'yellow'}
                              disabled={disabled || row.confirm}
                              onClick={() =>
                                updateRow(row.rowId, { confirm: true })
                              }
                            >
                              {row.confirm
                                ? 'Normalized address accepted'
                                : 'Use normalized address'}
                            </Button>
                          </Stack>
                        </Alert>
                      ) : row.status.state === 'error' ? (
                        <Text size="xs" c="red" mt={4}>
                          {row.status.message}
                        </Text>
                      ) : null}
                    </Table.Td>
                    <Table.Td valign="top">
                      <Checkbox
                        checked={row.confirm}
                        aria-label={`Row ${
                          index + 1
                        } confirmed normalized address`}
                        disabled={
                          disabled || row.status.state !== 'confirmation'
                        }
                        onChange={(event) =>
                          updateRow(row.rowId, {
                            confirm: event.currentTarget.checked,
                          })
                        }
                      />
                    </Table.Td>
                    <Table.Td valign="top">
                      <StatusBadge row={row} />
                      {row.status.state === 'success' &&
                      row.status.locationName ? (
                        <Text size="xs" c="dimmed" mt={4}>
                          {row.status.locationName}
                        </Text>
                      ) : null}
                    </Table.Td>
                    <Table.Td valign="top">
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="Duplicate row">
                          <ActionIcon
                            variant="subtle"
                            aria-label="Duplicate row"
                            disabled={row.status.state === 'loading'}
                            onClick={() =>
                              setRows((current) => [
                                ...current.slice(0, index + 1),
                                {
                                  ...row,
                                  rowId: nanoid(),
                                  status: { state: 'idle' },
                                  confirm: false,
                                  showValidation: false,
                                  touched: {},
                                },
                                ...current.slice(index + 1),
                              ])
                            }
                          >
                            <CopyIcon size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete row">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label="Delete row"
                            disabled={
                              rows.length === 1 ||
                              row.status.state === 'loading'
                            }
                            onClick={() => deleteRow(row)}
                          >
                            <TrashIcon size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Button
                    variant="subtle"
                    leftSection={<ArrowCircleDownIcon size={18} />}
                    onClick={() =>
                      setRows((current) => [...current, defaultRow()])
                    }
                  >
                    New row
                  </Button>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
    </RouteAppShell>
  );
}
